import React, { FC, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { ApolloError } from '@apollo/client/errors'
import { GET_PRODUCT, REMOVE_PRODUCT, UPDATE_PRODUCT } from 'queries/products'
import { updateProduct, updateProductVariables } from '__generatedTypes__/updateProduct'
import { getProduct, getProductVariables } from '__generatedTypes__/getProduct'
import { removeProduct, removeProductVariables } from '__generatedTypes__/removeProduct'
import { PageLoader } from 'components/PageLoader'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { toast } from 'utils/toast'
import { ProductTypeEnum } from '__generatedTypes__/globalTypes'
import { FIND_ALL_UPGRADE_PAGES } from 'queries/upgradePage'
import { getUpgradePageQueryVariables } from 'pagesNew/UpgradePage/ProductPricing/getUpgradePageQueryVariables'
import { ImageField } from 'components/DropzoneField'
import { STRIPE_PRODUCT_IMAGES_PATH_S3 } from 'constants/images'
import { isImageUploadError, loadImageFile, uploadImageFile } from 'utils/imageFile'
import { Form, FormValues } from '../components/Form'

const getMembershipTypeLabel = (type: ProductTypeEnum) => {
  if (type === ProductTypeEnum.Premium) {
    return 'Premium'
  }

  if (type === ProductTypeEnum.PremiumMax) {
    return 'Premium Max'
  }

  if (type === ProductTypeEnum.Extension) {
    return 'Extension'
  }

  if (type === ProductTypeEnum.Free) {
    return 'Free'
  }

  return 'Unknown'
}

const normalizeComparableValue = (key: string, value: unknown) => {
  if (key === 'description') {
    return value ?? ''
  }

  return value
}

const getDiffValues = (id: string, isExtension: boolean, initialValues: FormValues | object, values: FormValues) => {
  const input = isExtension
    ? {
        duration: values.duration,
        name: values.name,
        price: values.price,
        originalPrice: values.originalPrice,
        description: values.description ?? '',
      }
    : {
        duration: values.duration,
        name: values.name,
        price: values.price,
      }

  const transformInputValue = (key: string, value: unknown) => {
    if (key === 'originalPrice') {
      return value == null ? null : convertToCents(value as number)
    }

    if (key === 'price') {
      return convertToCents(value as number)
    }

    if (key === 'duration') {
      return String(value)
    }

    return value
  }

  return Object.keys(input).reduce(
    (acc, key) => {
      const inputKey = key as keyof typeof input
      const initialValueKey = key as keyof typeof initialValues
      const nextValue = normalizeComparableValue(inputKey, input[inputKey])
      const prevValue = normalizeComparableValue(inputKey, initialValues[initialValueKey])

      if (nextValue !== prevValue) {
        return {
          ...acc,
          [key]: transformInputValue(inputKey, input[inputKey]),
        }
      }

      return acc
    },
    { id: String(id) || '' }
  )
}

type EditMembershipProps = {
  isExtension?: boolean
}

export const EditMembership: FC<EditMembershipProps> = withPlatformCheck(
  ({ isExtension, platform }: EditMembershipProps & { platform: Platform }) => {
    const { id } = useParams()
    const { navigate } = useLink()
    const [imageField, setImageField] = useState<ImageField>()
    const [isImageLoading, setIsImageLoading] = useState(true)

    const { data, loading } = useQuery<getProduct, getProductVariables>(GET_PRODUCT, {
      variables: {
        platformName: platform.title,
        productId: String(id),
      },
      onCompleted: async (data) => {
        const imageUrl = data.getProduct?.image
        try {
          const loadedImage = await loadImageFile(imageUrl)

          if (loadedImage && imageUrl) {
            setImageField({ ...loadedImage, initialValue: imageUrl })
          }
        } catch (error) {
          console.error('Failed to load image', error)
          toast.error('Failed to load image')
        } finally {
          setIsImageLoading(false)
        }
      },
      onError: () => {
        navigate(cmsRoutes.PRODUCTS, undefined, {
          searchParams: new URLSearchParams({ tab: isExtension ? 'extensions' : 'memberships' }),
        })
      },
      fetchPolicy: 'network-only',
      skip: !id,
    })

    const product = data?.getProduct
    const isSubscriptionMembership = product?.isSubscription ?? false

    const [updateProductMutation] = useMutation<updateProduct, updateProductVariables>(UPDATE_PRODUCT)
    const [removeProductMutation] = useMutation<removeProduct, removeProductVariables>(REMOVE_PRODUCT)
    const getInitialValues = (): FormValues | undefined => {
      if (!product) {
        return
      }

      return {
        type: getMembershipTypeLabel(product.type),
        name: product.name,
        duration: isSubscriptionMembership ? (product.duration === '30' ? 'Month' : 'Year') : product.duration,
        price: convertToDollars(product.price),
        description: product.description ?? '',
        originalPrice: product.originalPrice != null ? convertToDollars(product.originalPrice) : undefined,
        image: imageField && [imageField],
      }
    }

    const handleSubmit = async (values: FormValues) => {
      try {
        const initialValues = getInitialValues() || {}

        const image = isExtension ? await uploadImageFile(values.image?.[0], STRIPE_PRODUCT_IMAGES_PATH_S3) : undefined
        const isImageChanged = image !== undefined && image !== (initialValues as FormValues).image?.[0]?.initialValue

        const fieldDiff = getDiffValues(String(id) || 'id', isExtension ?? false, initialValues, values)
        const hasFieldChanges = Object.keys(fieldDiff).some((key) => key !== 'id')

        if (!hasFieldChanges && !isImageChanged) {
          return toast.success('No changes to update')
        }

        const transformedInput = {
          ...fieldDiff,
          ...(isImageChanged && { image }),
        }

        await updateProductMutation({
          variables: {
            stripeProductInput: transformedInput,
          },
          refetchQueries: [
            {
              query: FIND_ALL_UPGRADE_PAGES,
              variables: getUpgradePageQueryVariables(platform.id, !isExtension),
            },
          ],
          awaitRefetchQueries: true,
        })

        navigate(cmsRoutes.PRODUCTS, undefined, {
          searchParams: new URLSearchParams({ tab: isExtension ? 'extensions' : 'memberships' }),
        })

        toast.success(`${isExtension ? 'Extension' : 'Membership'} updated successfully`)
      } catch (error) {
        if (error && typeof error === 'object' && 'graphQLErrors' in error) {
          return toast.error((error as ApolloError).graphQLErrors?.[0]?.message as string)
        }

        if (isImageUploadError(error)) {
          return toast.error(error.message)
        }

        return toast.error(`Failed to update ${isExtension ? 'extension' : 'membership'}`)
      }
    }

    const handleDelete = async () => {
      try {
        await removeProductMutation({
          variables: { productId: String(id) },
          refetchQueries: [
            {
              query: FIND_ALL_UPGRADE_PAGES,
              variables: getUpgradePageQueryVariables(platform.id, !isExtension),
            },
          ],
          awaitRefetchQueries: true,
        })

        navigate(cmsRoutes.PRODUCTS, undefined, {
          searchParams: new URLSearchParams({ tab: isExtension ? 'extensions' : 'memberships' }),
        })

        toast.success(`${isExtension ? 'Extension' : 'Membership'} deleted successfully`)
      } catch (error) {
        toast.error(`Failed to delete ${isExtension ? 'extension' : 'membership'}`)

        throw error
      }
    }

    if (loading || isImageLoading) {
      return <PageLoader />
    }

    return (
      <Form
        getInitialValues={getInitialValues}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        isExtension={isExtension}
        isSubscriptionMembership={isSubscriptionMembership}
      />
    )
  }
)
